/**
 * 系统标准字段定义清单
 *
 * 用于：
 *  - 字段映射模块右栏（目标字段候选列表）
 *  - 智能匹配算法（smartMatchColumns）的目标字段集合
 *  - 预览校验（必填字段缺失提示）
 *
 * 必填规则：phone 与 wechat 至少其一（业务校验在 service 层 / preview 中执行）；
 * 其他字段可选。具体字段含义详见 design.md §6.5（字段映射）与 requirements.md 4.x / 5.x / 6.x。
 */
import type { ISystemField } from '../types/mapping';
import {
  CUSTOM_FIELD_COLUMN_WIDTH_DEFAULT,
  FieldInputComponent,
  FieldType,
} from './custom-field';
import { INTENT_LEVEL_LABEL, IntentLevel } from './intent-level';
import { INTENT_LEVEL_COLOR } from './intent-level';
import { CRM_RECORD_STATUS_LABEL, CrmRecordStatus } from './crm-record-status';
import { CRM_RECORD_STATUS_COLOR } from './crm-record-status';
import type { ICustomFieldOption } from '../types/custom-field';

/** 系统标准字段清单（客资表的关键字段） */
export const SYSTEM_FIELDS: readonly ISystemField[] = [
  { key: 'name', label: '客户姓名', required: false, aliases: ['姓名', '客户姓名', '联系人'] },
  {
    key: 'phone',
    label: '手机号',
    required: false,
    aliases: ['手机号', '电话', '手机', '联系电话', '联系方式', 'mobile'],
  },
  { key: 'wechat', label: '微信号', required: false, aliases: ['微信', '微信号', '微信ID', 'wx'] },
  {
    key: 'platformLeadId',
    label: '平台客资ID',
    required: false,
    aliases: ['平台ID', 'lead_id', 'platform_lead_id'],
  },
  { key: 'sourceAccount', label: '来源账号', required: false, aliases: ['来源账号', '账号'] },
  { key: 'adPlanId', label: '广告计划', required: false, aliases: ['广告计划', 'campaign'] },
  { key: 'acquisitionLink', label: '获客链接', required: false, aliases: ['获客链接', 'link'] },
  { key: 'liveRoomId', label: '直播间归因', required: false, aliases: ['直播间', '直播间ID'] },
  { key: 'platformTags', label: '平台标签', required: false, aliases: ['标签', '平台标签', 'tags'] },
  { key: 'intentLevel', label: '意向等级', required: false, aliases: ['意向', '意向等级'] },
  { key: 'intentNote', label: '意向备注', required: false, aliases: ['备注', '需求', '意向备注'] },
];

/** 必填字段池：phone / wechat 至少其一（在 preview / webhook / import 中校验） */
export const SYSTEM_FIELDS_AT_LEAST_ONE: readonly string[] = ['phone', 'wechat'];

/**
 * 兼容旧版本的 customFields 容器名。
 *
 * 新版 CRM 动态字段保存为业务文档根字段；这里仅把旧容器名保留为禁用 fieldKey，
 * 避免旧数据兼容路径与新增根字段发生冲突。
 */
export const CUSTOM_FIELDS_CONTAINER_KEY = 'customFields';

/** CRM 系统字段定义形状，用于后端后补字段定义 */
export interface CrmSystemFieldDefinition {
  entityType: string;
  fieldKey: string;
  label: string;
  type: FieldType;
  inputComponent?: FieldInputComponent;
  required: boolean;
  order: number;
  columnWidth?: number;
  systemUsage: string[];
  options?: ICustomFieldOption[];
  showInForm?: boolean;
  showInList?: boolean;
  showInDetail?: boolean;
  showInFilter?: boolean;
}

function labelOptions<T extends string>(
  labels: Record<T, string>,
  colors?: Partial<Record<T, ICustomFieldOption['color']>>,
): ICustomFieldOption[] {
  return Object.entries(labels).map(([value, label]) => ({
    label: String(label),
    value,
    color: colors?.[value as T],
  }));
}

const CUSTOMER_GRADE_OPTIONS = ['A', 'B', 'C', 'D'].map((value) => ({
  label: `${value}级`,
  value,
}));

function crmField(
  entityType: string,
  fieldKey: string,
  label: string,
  type: FieldType,
  required: boolean,
  order: number,
  systemUsage: string[],
  ui: Partial<
    Pick<
      CrmSystemFieldDefinition,
      | 'inputComponent'
      | 'options'
      | 'columnWidth'
      | 'showInForm'
      | 'showInList'
      | 'showInDetail'
      | 'showInFilter'
    >
  > = {},
): CrmSystemFieldDefinition {
  const showInForm = ui.showInForm ?? systemUsage.includes('form');
  return {
    entityType,
    fieldKey,
    label,
    type,
    inputComponent: ui.inputComponent ?? FieldInputComponent.DEFAULT,
    required,
    order,
    columnWidth: ui.columnWidth ?? CUSTOM_FIELD_COLUMN_WIDTH_DEFAULT,
    systemUsage,
    showInForm,
    showInList: ui.showInList ?? false,
    showInDetail: ui.showInDetail ?? true,
    showInFilter: ui.showInFilter ?? systemUsage.includes('filter'),
    options: ui.options ?? [],
  };
}

/** 现有 CRM 系统字段：补入字段定义时标记 system=true */
export const CRM_SYSTEM_FIELD_DEFINITIONS: Record<string, readonly CrmSystemFieldDefinition[]> = {
  'crm-record': [
    crmField('crm-record', 'recordCode', '记录编号', FieldType.TEXT, true, 10, ['identity'], { showInList: true }),
    crmField('crm-record', 'platformCode', '来源平台', FieldType.TEXT, false, 20, ['source', 'dedup', 'filter'], {
      inputComponent: FieldInputComponent.PLATFORM_SELECT,
      showInForm: true,
      showInList: true,
      showInFilter: true,
    }),
    crmField('crm-record', 'platformLeadId', '平台客资ID', FieldType.TEXT, false, 30, ['dedup']),
    crmField('crm-record', 'sourceAccountId', '来源账号ID', FieldType.TEXT, false, 35, ['source'], {
      showInForm: false,
    }),
    crmField('crm-record', 'sourceAccount', '来源账号', FieldType.TEXT, false, 36, ['source', 'filter'], {
      showInList: true,
      showInFilter: true,
    }),
    crmField('crm-record', 'name', '客户姓名', FieldType.TEXT, true, 40, ['profile', 'form'], { showInList: true }),
    crmField('crm-record', 'phone', '手机号', FieldType.TEXT, false, 50, ['dedup', 'privacy', 'form'], {
      showInList: true,
    }),
    crmField('crm-record', 'wechat', '微信号', FieldType.TEXT, false, 60, ['dedup', 'privacy', 'form'], {
      showInList: true,
    }),
    crmField('crm-record', 'region', '地区', FieldType.TEXT, false, 70, ['filter', 'form'], {
      inputComponent: FieldInputComponent.REGION_CASCADER,
      showInFilter: true,
    }),
    crmField('crm-record', 'intentLevel', '意向等级', FieldType.SELECT, false, 80, ['status', 'form', 'filter'], {
      options: labelOptions(
        INTENT_LEVEL_LABEL as Record<IntentLevel, string>,
        INTENT_LEVEL_COLOR as Partial<Record<IntentLevel, ICustomFieldOption['color']>>,
      ),
      showInList: true,
      showInFilter: true,
    }),
    crmField('crm-record', 'intentNote', '意向备注', FieldType.TEXTAREA, false, 90, ['profile', 'form']),
    crmField('crm-record', 'ownerOrgId', '所属公司', FieldType.TEXT, true, 100, ['scope', 'form'], {
      inputComponent: FieldInputComponent.ORG_TREE_SELECT,
      showInForm: true,
      showInList: true,
    }),
    crmField('crm-record', 'assignedTo', '负责人', FieldType.TEXT, false, 110, ['scope', 'assign'], {
      showInList: true,
    }),
    crmField('crm-record', 'status', '客资状态', FieldType.SELECT, true, 120, ['state-machine', 'filter'], {
      options: labelOptions(
        CRM_RECORD_STATUS_LABEL as Record<CrmRecordStatus, string>,
        CRM_RECORD_STATUS_COLOR as Partial<Record<CrmRecordStatus, ICustomFieldOption['color']>>,
      ),
      showInList: true,
      showInFilter: true,
    }),
    crmField('crm-record', 'returnReasonDetail', '退回原因', FieldType.TEXTAREA, false, 130, ['state-machine'], {
      showInForm: false,
    }),
    crmField('crm-record', 'opportunityName', '商机名称', FieldType.TEXT, false, 140, ['opportunity', 'form'], {
      showInList: true,
    }),
    crmField('crm-record', 'expectedAmount', '预期金额', FieldType.MONEY, false, 160, ['forecast', 'form'], {
      showInList: true,
    }),
    crmField(
      'crm-record',
      'expectedCloseDate',
      '预计成交日',
      FieldType.DATE,
      false,
      170,
      ['forecast', 'form', 'filter'],
      { showInList: true, showInFilter: true },
    ),
    crmField('crm-record', 'dealAt', '成交日期', FieldType.DATE, false, 180, ['deal'], {
      showInForm: false,
      showInList: true,
    }),
    crmField('crm-record', 'convertedAmount', '成交总额', FieldType.MONEY, false, 190, ['deal'], {
      showInForm: false,
      showInList: true,
    }),
    crmField('crm-record', 'dealProducts', '成交产品', FieldType.MULTISELECT, false, 200, ['deal'], {
      showInForm: false,
      showInList: true,
      columnWidth: 220,
    }),
    crmField('crm-record', 'customerGrade', '客户等级', FieldType.SELECT, false, 210, ['customer', 'filter'], {
      options: CUSTOMER_GRADE_OPTIONS,
      showInList: true,
      showInFilter: true,
    }),
    crmField('crm-record', 'customerTags', '客户标签', FieldType.MULTISELECT, false, 220, ['customer', 'form']),
    crmField('crm-record', 'lastInteractionAt', '最后互动', FieldType.DATE, false, 230, ['activity'], {
      showInForm: false,
      showInList: true,
    }),
    crmField('crm-record', 'createdAt', '创建时间', FieldType.DATE, false, 240, ['audit'], {
      showInForm: false,
      showInList: true,
      showInDetail: true,
    }),
    crmField('crm-record', 'updatedAt', '更新时间', FieldType.DATE, false, 250, ['audit'], {
      showInForm: false,
      showInList: true,
      showInDetail: true,
    }),
  ],
};

/** 当前运行时仅保留统一 CRM 记录固定字段。 */
export const FIXED_FIELDS_BY_ENTITY: Record<string, readonly string[]> = {
  'crm-record': CRM_SYSTEM_FIELD_DEFINITIONS['crm-record'].map((field) => field.fieldKey),
};

/**
 * 判断给定 EntityType 下某 Field_Key 是否与固定字段（Fixed_Field）冲突。
 *
 * 规则：
 *  - 命中所属 EntityType 的固定字段清单 → 保留（返回 true）；
 *  - 旧 customFields 兼容容器名在任意实体下均视为保留（不可作为 Field_Key）；
 *  - 未知 EntityType 的固定字段集合视为空，仅保留 customFields 判定。
 *
 * 纯函数，无副作用，便于属性测试（design.md Property 5）。
 * Requirements: 2.4、2.5
 *
 * @param entityType 业务实体类型，如 'crm-record'
 * @param fieldKey 待校验的自定义字段标识
 * @returns 是否为保留字段（与固定字段或容器名冲突）
 */
export function isReservedFieldKey(entityType: string, fieldKey: string): boolean {
  // 旧 customFields 兼容容器名在任意实体下都不可作为 Field_Key
  if (fieldKey === CUSTOM_FIELDS_CONTAINER_KEY) {
    return true;
  }
  const fixedFields = FIXED_FIELDS_BY_ENTITY[entityType] ?? [];
  return fixedFields.includes(fieldKey);
}
